import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import ConfirmDialog from "@/components/ConfirmDialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Youtube, Trash2, Plus, ArrowUp, ArrowDown, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function AdminLandingVideosPage() {
  const { user } = useAuth();
  const [videos, setVideos] = useState(null);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [adding, setAdding] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/landing/videos");
      setVideos(data);
    } catch {
      setVideos([]);
    }
  };

  useEffect(() => { load(); }, []);

  if (!user) return null;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const add = async (e) => {
    e.preventDefault();
    if (!url.trim()) {
      toast.error("Paste a YouTube URL first");
      return;
    }
    setAdding(true);
    try {
      await api.post("/admin/landing/videos", { url: url.trim(), title: title.trim() || null });
      toast.success("Video added to the landing carousel");
      setUrl("");
      setTitle("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add the video");
    } finally {
      setAdding(false);
    }
  };

  const move = async (idx, dir) => {
    if (!videos || videos.length < 2) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= videos.length) return;
    const reordered = [...videos];
    const [v] = reordered.splice(idx, 1);
    reordered.splice(newIdx, 0, v);
    setVideos(reordered); // optimistic
    try {
      await api.post("/admin/landing/videos/reorder", {
        video_ids: reordered.map((x) => x.video_id),
      });
    } catch {
      toast.error("Couldn't save new order");
      load();
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/landing/videos/${pendingDelete.video_id}`);
      toast.success("Removed from carousel");
      setPendingDelete(null);
      load();
    } catch {
      toast.error("Couldn't remove the video");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="mb-8">
        <div className="label-eyebrow text-[#002FA7] flex items-center gap-2">
          <Youtube size={12} /> Admin · Landing videos
        </div>
        <h1 className="text-4xl sm:text-5xl tracking-tight font-black text-[#111827]">Front-page videos</h1>
        <p className="text-sm text-[#4B5563] mt-2 max-w-2xl">
          Add YouTube videos to the homepage carousel at <span className="font-mono">trackmatrix.net</span>. Visitors see them in the order you set below. Removing a video here removes it from the front page instantly.
        </p>
      </div>

      {/* Add form */}
      <form onSubmit={add} className="card-flat p-6 mb-8 space-y-3" data-testid="admin-add-video-form">
        <div>
          <label className="label-eyebrow text-[#4B5563]">YouTube URL</label>
          <input
            data-testid="admin-add-video-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=… or https://youtu.be/…"
            className="input-base mt-2 font-mono text-xs"
          />
          <p className="text-xs text-[#9CA3AF] mt-1">Works with regular YouTube URLs, youtu.be short links, and Shorts.</p>
        </div>
        <div>
          <label className="label-eyebrow text-[#4B5563]">Title <span className="text-[#9CA3AF] font-normal lowercase">(optional)</span></label>
          <input
            data-testid="admin-add-video-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Shown under the video on the homepage"
            className="input-base mt-2"
          />
        </div>
        <button type="submit" disabled={adding} data-testid="admin-add-video-submit" className="btn-primary inline-flex items-center gap-2">
          <Plus size={14} /> {adding ? "Adding…" : "Add video"}
        </button>
      </form>

      {/* List */}
      <div className="label-eyebrow text-[#4B5563] mb-3">In the carousel ({videos?.length ?? "—"})</div>
      {videos === null ? (
        <div className="card-flat h-40 animate-pulse bg-[#F9FAFB]" />
      ) : videos.length === 0 ? (
        <div className="card-flat p-12 text-center text-sm text-[#4B5563]">
          No videos yet. Paste a YouTube URL above to add the first one.
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-videos-list">
          {videos.map((v, idx) => (
            <div key={v.video_id} className="card-flat p-3 flex items-center gap-3" data-testid={`admin-video-row-${v.video_id}`}>
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  data-testid={`admin-video-up-${v.video_id}`}
                  className="h-7 w-7 rounded inline-flex items-center justify-center text-[#4B5563] hover:bg-[#F3F4F6] disabled:opacity-30"
                  title="Move up"
                >
                  <ArrowUp size={14} />
                </button>
                <button
                  onClick={() => move(idx, +1)}
                  disabled={idx === videos.length - 1}
                  data-testid={`admin-video-down-${v.video_id}`}
                  className="h-7 w-7 rounded inline-flex items-center justify-center text-[#4B5563] hover:bg-[#F3F4F6] disabled:opacity-30"
                  title="Move down"
                >
                  <ArrowDown size={14} />
                </button>
              </div>
              <img src={v.thumbnail_url} alt="" className="h-16 w-28 object-cover rounded-md border border-[#E5E7EB] flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-bold text-[#111827] truncate">{v.title || "(no title)"}</div>
                <div className="font-mono text-xs text-[#9CA3AF] truncate">{v.youtube_id}</div>
              </div>
              <a
                href={`https://www.youtube.com/watch?v=${v.youtube_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="h-9 w-9 inline-flex items-center justify-center rounded-md text-[#4B5563] hover:bg-[#F3F4F6]"
                title="Open on YouTube"
              >
                <ExternalLink size={14} />
              </a>
              <button
                onClick={() => setPendingDelete(v)}
                data-testid={`admin-video-delete-${v.video_id}`}
                className="h-9 w-9 inline-flex items-center justify-center rounded-md text-[#EF4444] hover:bg-[#FEF2F2]"
                title="Remove"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Remove this video?"
        description={pendingDelete ? `"${pendingDelete.title || pendingDelete.youtube_id}" will be removed from the homepage carousel immediately.` : ""}
        confirmLabel="Remove"
        busy={deleting}
        onCancel={() => !deleting && setPendingDelete(null)}
        onConfirm={confirmDelete}
      />
    </DashboardLayout>
  );
}
